// App.tsx — Top-level router for the increment SPA.
//
// Auth UX is OIDC redirect against authz.lab0.us. The token's scopes
// determine what the UI exposes — `increment.view` for read, `increment.admin`
// for the Admin tab. CI service accounts can fall back to paste-a-JWT.
//
// Chrome: shared `Shell` from lib/web carries the standard Bootswatch
// navbar (brand, nav, theme toggle, logout). Surfaces & buttons follow
// Bootswatch's Darkly/Flatly tokens — DESIGN.md C-SURFACE-1.
//
// Routes: a repo is one page now (experiments pane + gitgraph + commit
// overview), so the old per-commit page and the live-metrics (curves) page
// are gone. Links to a commit page are kept alive by a redirect into the
// repo page's commit selection.
import {
  BrowserRouter, Routes, Route, Navigate, useLocation, useParams,
} from 'react-router-dom';
import { BsGraphUp } from 'react-icons/bs';
import { useAuth, Shell, type ShellNavItem } from 'common';
import { apiClient, hasScope } from './api';
import ReposPage from './pages/ReposPage';
import RepoPage from './pages/RepoPage';
import MetricPage from './pages/MetricPage';
import AdminPage from './pages/AdminPage';
import LoginPage from './pages/LoginPage';

function ShelledRoutes({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const auth = useAuth({ client: apiClient });

  if (auth.loading) {
    return <div className="empty-state">Loading…</div>;
  }
  if (!auth.authenticated && pathname !== '/login') {
    return <Navigate to="/login" replace />;
  }

  const isAdmin = hasScope(auth.scopes, 'increment.admin');
  const navItems: ShellNavItem[] = [{ label: 'Repos', href: '/' }];
  if (isAdmin) navItems.push({ label: 'Admin', href: '/admin' });

  const handleLogout = () => {
    auth.logout();
    window.location.href = '/api/oidc/logout';
  };

  return (
    <Shell
      brand={<><BsGraphUp size={18} /> increment.lab0.us</>}
      brandHref="/"
      navItems={navItems}
      userEmail={auth.authenticated ? auth.email : undefined}
      userPhotoUrl={auth.authenticated ? auth.photoUrl : undefined}
      onLogout={handleLogout}
    >
      {children}
    </Shell>
  );
}

// The commit page was folded into the repo page's overview pane. Old links
// (and the ones the rsna notebooks print) keep working by redirecting into
// the selection the pane reads.
function CommitRedirect() {
  const { owner, name, sha } = useParams<{ owner: string; name: string; sha: string }>();
  if (!owner || !name || !sha) return <Navigate to="/" replace />;
  return <Navigate to={`/repos/${owner}/${name}?sel=${encodeURIComponent(sha)}`} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/*"
          element={
            <ShelledRoutes>
              <Routes>
                <Route path="/" element={<ReposPage />} />
                <Route path="/repos/:owner/:name" element={<RepoPage />} />
                <Route
                  path="/repos/:owner/:name/commits/:sha"
                  element={<CommitRedirect />}
                />
                <Route
                  path="/repos/:owner/:name/metrics/:metric"
                  element={<MetricPage />}
                />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </ShelledRoutes>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
